import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

import { useJsonApi } from "@/modules/api/hooks/useJsonApi";
import { queryKeys } from "@/modules/api/queryKeys";
import { apiErrorValidate } from "@/modules/api/utils/apiErrorValidate";
import {
  getLevelsQuery,
  GetLevelsResponse,
} from "@/modules/levels/api/queries/getLevelsQuery";
import { useUserDataContext } from "@/modules/user/hooks/useUserDataContext";

const translationNamespace = "apiMessages/useGetLevelsQuery";

export function useGetLevelsQuery() {
  const jsonApi = useJsonApi();
  const { t } = useTranslation(translationNamespace);

  const { currentGroup } = useUserDataContext();
  return useQuery(
    currentGroup
      ? {
          queryKey: [queryKeys.levels.getLevelsList, currentGroup],
          queryFn: () =>
            getLevelsQuery(jsonApi, {
              userGroupId: currentGroup.id,
            }),
          retry: false,
          refetchOnWindowFocus: false,
          select: (response) => {
            if (response) {
              return parseLevelsList(response);
            }
          },
          onError: (error) => {
            apiErrorValidate(error, {
              401: {
                UNAUTHORIZED: t(
                  "You are not allowed to view levels. Please sign in again"
                ),
              },
              403: {
                ACCESS_FORBIDDEN: t("You don't have an access to view levels"),
              },
            });
          },
        }
      : { enabled: false }
  );
}

export type ParsedLevel = {
  name: string;
  order: number;
  required: {
    seniorityValue: number;
    seniorityUnit: "DAYS" | "MONTHS" | "WEEKS" | "YEARS";
    pointsNumber: number;
    averageRating: number;
    seniorityDays: number;
    tutorialCount: number;
  };
  achieved: {
    tutorialCount: number;
    pointsNumber: number;
    averageRating: number;
    seniorityDays: number;
  };
};

function parseLevelsList(response: GetLevelsResponse): ParsedLevel[] {
  return response.data.map(({ attributes, meta }) => ({
    name: attributes.name,
    order: attributes.order,
    required: {
      seniorityValue: attributes.seniorityValue,
      seniorityUnit: attributes.seniorityUnit,
      pointsNumber: attributes.pointsNumber,
      averageRating: attributes.averageRating,
      seniorityDays: attributes.seniorityDays,
      tutorialCount: meta.tutorialCount,
    },
    achieved: {
      tutorialCount: meta.progress.completedTutorialCount,
      pointsNumber: meta.progress.pointsNumber,
      averageRating: meta.progress.averageRating,
      seniorityDays: meta.progress.seniorityDays,
    },
  }));
}
